import { type ChatMessage, type ChatOptions } from "@livekit/components-core";
import {
  ChatCloseIcon,
  ChatEntry,
  ChatToggle,
  type MessageFormatter,
  useChat,
  useMaybeLayoutContext,
} from "@livekit/components-react";
import * as React from "react";

/** @public */
export interface ChatProps
  extends React.HTMLAttributes<HTMLDivElement>, ChatOptions {
  messageFormatter?: MessageFormatter;
}

/**
 * This is a modified livekit chat component that uses a text area for the input.
 * The Chat component provides ready-to-use chat functionality in a LiveKit room.
 * Messages are distributed to all participants in the room in real-time.
 *
 * @remarks
 * - Only users who are in the room at the time of dispatch will receive messages
 * - Message history is not persisted between sessions
 * - Requires `@livekit/components-styles` to be imported for styling
 *
 * @example
 * ```tsx
 * import '@livekit/components-styles';
 *
 * function Room() {
 *   return (
 *     <LiveKitRoom data-lk-theme="default">
 *       <Chat />
 *     </LiveKitRoom>
 *   );
 * }
 * ```
 *
 * For custom styling, refer to: https://docs.livekit.io/reference/components/react/concepts/style-components/
 *
 * @public
 */
export function Chat({
  messageFormatter,
  messageDecoder,
  messageEncoder,
  channelTopic,
  ...props
}: ChatProps) {
  const ulRef = React.useRef<HTMLUListElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const chatOptions: ChatOptions = React.useMemo(() => {
    return { messageDecoder, messageEncoder, channelTopic };
  }, [messageDecoder, messageEncoder, channelTopic]);
  const { chatMessages, send, isSending } = useChat(chatOptions);
  const layoutContext = useMaybeLayoutContext();
  const lastReadMsgAt = React.useRef<ChatMessage["timestamp"]>(0);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (inputRef.current && inputRef.current.value.trim() !== "") {
      await send(inputRef.current.value);
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  }

  React.useEffect(() => {
    if (ulRef) {
      ulRef.current?.scrollTo({ top: ulRef.current.scrollHeight });
    }
  }, [ulRef, chatMessages]);

  React.useEffect(() => {
    if (!layoutContext?.widget || chatMessages.length === 0) {
      return;
    }

    if (
      layoutContext.widget.state?.showChat &&
      chatMessages.length > 0 &&
      lastReadMsgAt.current !== chatMessages[chatMessages.length - 1]?.timestamp
    ) {
      lastReadMsgAt.current = chatMessages[chatMessages.length - 1]?.timestamp;
      return;
    }

    const unreadMessageCount = chatMessages.filter(
      (msg) => !lastReadMsgAt.current || msg.timestamp > lastReadMsgAt.current,
    ).length;

    if (
      unreadMessageCount > 0 &&
      layoutContext?.widget.state?.unreadMessages !== unreadMessageCount
    ) {
      layoutContext?.widget.dispatch?.({
        msg: "unread_msg",
        count: unreadMessageCount,
      });
    }
  }, [chatMessages, layoutContext?.widget]);

  return (
    <div
      {...props}
      className="w-[300px] h-fill flex h-screen max-h-screen flex-1 flex-col justify-between bg-[#181818]"
    >
      <div className="flex w-full shrink-0 flex-row items-center justify-between p-2">
        <div className="flex w-full text-sm font-bold">Messages</div>

        <ChatToggle className="lk-close-button">
          <ChatCloseIcon />
        </ChatToggle>
      </div>

      <ul
        className="lk-list lk-chat-messages pb-2! min-h-0 grow overflow-y-auto"
        ref={ulRef}
      >
        {chatMessages.map((msg, idx, allMsg) => {
          const hideName = idx >= 1 && allMsg[idx - 1].from === msg.from;
          // If the time delta between two messages is bigger than 60s show timestamp.
          const hideTimestamp =
            idx >= 1 && msg.timestamp - allMsg[idx - 1].timestamp < 60_000;

          return (
            <ChatEntry
              key={msg.id ?? idx}
              hideName={hideName}
              hideTimestamp={hideName === false ? false : hideTimestamp} // If we show the name always show the timestamp as well.
              entry={msg}
              messageFormatter={messageFormatter}
            />
          );
        })}
      </ul>

      <form
        className="mb-2 flex w-full shrink-0 gap-2 p-2"
        onSubmit={handleSubmit}
      >
        <textarea
          className="p-2 rounded-md"
          disabled={isSending}
          ref={inputRef}
          placeholder="Enter a message..."
          onInput={(ev) => ev.stopPropagation()}
          onKeyDown={(ev) => ev.stopPropagation()}
          onKeyUp={(ev) => ev.stopPropagation()}
        />
        <button
          type="submit"
          className="lk-button lk-chat-form-button"
          disabled={isSending}
        >
          Send
        </button>
      </form>
    </div>
  );
}
